import React, { useState } from 'react'
import { UserStream } from 'components/filters/FilterIcon'
import { UserCheck, UserPlus } from 'lucide-react'
import api from 'api/api'
import styles from './user-card.module.scss'
import cn from 'classnames'

interface UserCardProps {
    user: {
        id: string
        name: string
        username?: string
        bio?: string
        description?: string
        md5_email?: string
        path?: string
        is_followed_by_current_user?: boolean
    }
    onFollowToggle?: (userId: string, isFollowing: boolean) => void
    onUserClick?: () => void
}

export default function UserCard({ user, onFollowToggle, onUserClick }: UserCardProps) {
    const [isFollowing, setIsFollowing] = useState(user.is_followed_by_current_user || false)
    const [isLoadingFollow, setIsLoadingFollow] = useState(false)

    const truncateDescription = (description: string) => {
        if (!description) return ''
        if (description.length <= 100) return description
        return `${description.substring(0, 100)}...`
    }

    const handleFollowToggle = async (e: React.MouseEvent) => {
        e.preventDefault()
        e.stopPropagation()

        if (isLoadingFollow) return

        setIsLoadingFollow(true)
        try {
            if (isFollowing) {
                await api.unfollowUser(user.id)
                setIsFollowing(false)
            } else {
                await api.followUser(user.id)
                setIsFollowing(true)
            }
            onFollowToggle?.(user.id, !isFollowing)
        } catch (error) {
            console.error('Error toggling follow:', error)
        } finally {
            setIsLoadingFollow(false)
        }
    }

    return (
        <div className={styles.userCard}>
            <div className={styles.userInfo}>
                <UserStream
                    user={user}
                    size="60"
                    contextStyles={styles.userIcon}
                    tip={`View ${user.username || user.name}'s stream`}
                    onAfterClick={onUserClick}
                />
                <div className={styles.userDetails}>
                    <div className={styles.userName}>{user.username || user.name || 'User'}</div>
                    <div className={styles.userDescription}>
                        {truncateDescription(user.bio || user.description || '')}
                    </div>
                </div>
            </div>
            {/* Shown for your own card too: users follow themselves by default so own
                posts land in Home, and this is where they can undo that. */}
            {gon.currentUser && (
                <button
                    className={cn(styles.followButton, isFollowing && styles.unfollowButton)}
                    onClick={handleFollowToggle}
                    disabled={isLoadingFollow}
                >
                    {isFollowing ? (
                        <>
                            <UserCheck size={16} />
                            <span>{isLoadingFollow ? 'Loading...' : 'Following'}</span>
                        </>
                    ) : (
                        <>
                            <UserPlus size={16} />
                            <span>{isLoadingFollow ? 'Loading...' : 'Follow'}</span>
                        </>
                    )}
                </button>
            )}
        </div>
    )
}
